import React, { useEffect, useState } from "react";
import { MenuItem, MenuList } from "@mui/material";
import MuiButton from "@components/Button/MuiButton";
import MuiCheckbox from "@components/Checkbox/MuiCheckbox";
import ApplicationFilterModal from "./ApplicationFilterModal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import SearchInput from "./Search";

// Types
type FilterType = "checkbox" | "dateRange" | "category";

interface FilterConfig {
  type: FilterType;
  label: string;
  key: string;
}

interface ApplicationFilterProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (filters: Record<string, any>) => void;
  filterData: {
    stages?: Array<{ id: number; name: string }>;
    access_categories?: Array<{ name: string; sub_categories: Array<{ id: number; name: string }> }>;
    row_categories?: Array<{ name: string; sub_categories: Array<{ id: number; name: string }> }>;
  };
  initialFilters?: Record<string, any>;
}

// Date fields mapping
const DATE_RANGE_KEYS = {
  creationDateRange: { start: "from", end: "to" }, 
};

const ApplicationFilter: React.FC<ApplicationFilterProps> = ({
  open,
  handleClose,
  handleSubmit,
  filterData = {},
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<Record<string, any>>(initialFilters);
  const [filterConfig, setFilterConfig] = useState<FilterConfig[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<FilterConfig | null>(null);
  const [searchedOptions, setSearchedOptions] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState({
    creationDateRange: { start: null, end: null },
  });


  // Filter config setup
  useEffect(() => {
    const config: FilterConfig[] = [];
    config.push({ type: "checkbox", label: "Stage", key: "stage" });
    config.push({ type: "category", label: "Access Categories", key: "access_categories" });
    config.push({ type: "category", label: "ROW Categories", key: "row_categories" });
    config.push({ type: "dateRange", label: "Creation Date Range", key: "creationDateRange" });
    setFilterConfig(config);
    if (config.length > 0 && !selectedFilter) setSelectedFilter(config[0]);
  }, [filterData]);

  useEffect(() => {
    if (open) {
      setFilters(initialFilters || {});
      setSearchedOptions([]);
    }
  }, [open]);

  // Methods
  const handleSelectFilter = (filter: FilterConfig) => { 
    setSelectedFilter(filter);
    setSearchedOptions([]);
  };

  // Checkbox filter change for all filter types
  const handleCheckboxFilter = (item: { id: number; name: string }, key: string) => {
    setFilters((prev) => {
      const arr = prev[key] || [];
      const index = arr.indexOf(item.id);
      let updated;
      if (index > -1) {
        updated = arr.filter((id: number) => id !== item.id);
      } else {
        updated = [...arr, item.id];
      }
      return { ...prev, [key]: updated.length ? updated : undefined };
    });
  }; 

  // Date change
  const handleDateRangeChange = (
    key: "start" | "end",
    value: dayjs.Dayjs | null,
    rangeType: keyof typeof DATE_RANGE_KEYS,
  ) => {
    if (value) {
      const now = dayjs();
      const combinedDateTime = value
        .hour(now.hour())
        .minute(now.minute())
        .second(now.second())
        .millisecond(now.millisecond());
      const localISOString = combinedDateTime.format("YYYY-MM-DDTHH:mm:ss.SSS");
      setDateRange((prev) => ({
        ...prev,
        [rangeType]: { ...prev[rangeType], [key]: localISOString },
      }));
    } else {
      setDateRange((prev) => ({
        ...prev,
        [rangeType]: { ...prev[rangeType], [key]: null },
      }));
    }
  };

  // Only used for stages search for now
  const handleSearch = (result: Record<string, string[]>) => {
    const names = result["stage"] || [];
    setSearchedOptions((filterData.stages || []).filter((option) => names.includes(option.name)));
  };

  // Returns options for the selected filter
  const getCurrentOptions = () => {
    if (!selectedFilter || selectedFilter.type !== "checkbox") return [];
    if (selectedFilter.key === "stage") {
      return filterData.stages || [];
    }
    if (selectedFilter.key === "access_categories_id") {
      return filterData.access_categories || [];
    }
    if (selectedFilter.key === "row_categories_id") {
      return filterData.row_categories || [];
    }
    return [];
  };

  const handleClear = () => {
    setFilters({});
    setDateRange({
      creationDateRange: { start: null, end: null },
      // scheduledDateRange: { start: null, end: null },
      // completionDateRange: { start: null, end: null },
    });
    if (filterConfig.length > 0) setSelectedFilter(filterConfig[0]);
  };

  // Final payload on submit
  const handleSubmitLocal = () => {
    const apiDateRanges = {
      [DATE_RANGE_KEYS.creationDateRange.start]: dateRange.creationDateRange.start,
      [DATE_RANGE_KEYS.creationDateRange.end]: dateRange.creationDateRange.end,
    };

    // Get all possible access/row subcategory IDs
    const allAccessIds = (filterData.access_categories || []).flatMap(cat => cat.sub_categories.map(sub => sub.id));
    const allRowIds = (filterData.row_categories || []).flatMap(cat => cat.sub_categories.map(sub => sub.id));

    // If all are selected, treat as no filter (undefined)
    const access_categories_id =
      filters.access_categories_id && filters.access_categories_id.length === allAccessIds.length
        ? undefined
        : filters.access_categories_id;
    const row_categories_id =
      filters.row_categories_id && filters.row_categories_id.length === allRowIds.length
        ? undefined
        : filters.row_categories_id;

    handleSubmit({
      ...(filters.stage ? { stage: filters.stage } : {}),
      ...(access_categories_id ? { access_categories_id } : {}),
      ...(row_categories_id ? { row_categories_id } : {}),
      ...apiDateRanges,
    });
    handleClose();
  };

  const hasDateRangeError = (() => {
    const range = dateRange[selectedFilter?.key as keyof typeof dateRange];

    if (!range?.start || !range?.end) return false;

    return dayjs(range.end).isBefore(dayjs(range.start), "day");
  })();

  if (!selectedFilter) return null;

  // --- UI ---
  return (
    <ApplicationFilterModal
      open={open}
      handleClose={handleClose}
      title="Filters"
      sx={{ minWidth: { xs: "90vw", md: "48rem" } }}
    >
      <div>
        <div className="divider-solid"></div>
        <div className="grid md:grid-cols-3 grid-cols-1 bg-gray-000">
          {/* Left Menu */}
          <div className="col-span-1 br-r-solid br-b-solid">
            <MenuList
              sx={{ paddingTop: 0, paddingBottom: 0, maxHeight: "35rem" }}
              className="overflow-y-auto hide-x-scrollbar"
            >
              {filterConfig.map((filter, idx) => (
                <MenuItem
                  key={idx}
                  onClick={() => handleSelectFilter(filter)}
                  sx={{
                    paddingBlock: "1rem",
                    paddingInline: ".7rem",
                    fontWeight:
                      selectedFilter?.key === filter.key ? "500" : "normal",
                    borderRight:
                      selectedFilter?.key === filter.key
                        ? {
                            md: "4px solid #3A1C73",
                            xs: "0px solid",
                          }
                        : "4px solid transparent",
                    background:
                      selectedFilter?.key === filter?.key
                        ? "#CFDAE74D"
                        : "transparent",
                  }}
                >
                  <div className="flex justify-between w-full items-center">
                    <span>{filter.label}</span>
                    {filter.type === "checkbox" &&
                      filters[filter.key]?.length > 0 && (
                        <span
                          className="ml-1 inline-grid place-item-center w-25 aspect-square items-center justify-center bg-brand-300 rounded-full p-1"
                          style={{ background: "#3A1C73", color: "#fff" }}
                        >
                          {filters[filter.key]?.length}
                        </span>
                      )}
                    {filter.type === "category" &&
                      (filters[
                        filter.key === "access_categories"
                          ? "access_categories_id"
                          : "row_categories_id"
                      ]?.length ?? 0) > 0 && (
                        <span
                          className="ml-1 inline-grid place-item-center w-25 aspect-square items-center justify-center bg-brand-300 rounded-full p-1"
                          style={{ background: "#3A1C73", color: "#fff" }}
                        >
                          {filters[
                            filter.key === "access_categories"
                              ? "access_categories_id"
                              : "row_categories_id"
                          ]?.length}
                        </span>
                      )}
                  </div>
                </MenuItem>
              ))}
            </MenuList>
          </div>
          {/* Right Panel */}
          <div
            className="col-span-2 mt-4 list-filter"
            style={{ overflowY: "auto", height: "29rem" }}
          >
            {selectedFilter.type === "checkbox" && selectedFilter.key === "stage" && (
              <>
                {/* Stage filter */}
                <div className="mb-4 px-6">
                  <SearchInput
                    data={{
                      stage: (filterData.stages || []).map((option) => option.name),
                    }}
                    selected="stage"
                    searchedResult={handleSearch}
                  />
                </div>
                <p className="px-6 text-xs font-medium text-brand-500">Select</p>
                <ul className="list-none px-3">
                  {(searchedOptions.length > 0 ? searchedOptions : (filterData.stages || [])).map((item, idx) => (
                    <li className="flex items-start" key={idx}>
                      <div className="pr-2">
                        <MuiCheckbox
                          onChange={() => handleCheckboxFilter(item, "stage")}
                          checked={filters.stage?.includes(item.id) || false}
                          sx={{
                            paddingTop: 0,
                            color: `var(--brand-200)`,
                            "&.Mui-checked": { color: `#3A1C73` },
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-800 pt-3">{item.name}</span>
                    </li>
                  ))}
                </ul>
              </>
            )} 
            {selectedFilter.type === "category" && selectedFilter.key === "access_categories" && (
              <>
                {/* Access Categories filter */}
                <div className="px-3">
                  {(filterData.access_categories || []).map((cat, idx) => (
                    <div key={cat.name} style={{ marginBottom: 12 }}>
                      <div className="font-semibold text-brand-700 mb-1">{cat.name}</div>
                      <ul className="list-none">
                        {cat.sub_categories.map((sub) => (
                          <li className="flex items-start" key={sub.id}>
                            <div className="pr-2">
                              <MuiCheckbox
                                onChange={() => handleCheckboxFilter(sub, "access_categories_id")}
                                checked={filters.access_categories_id?.includes(sub.id) || false}
                                sx={{
                                  paddingTop: 0,
                                  color: `var(--brand-200)`,
                                  "&.Mui-checked": { color: `#3A1C73` },
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-800 pt-3">{sub.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
            {selectedFilter.type === "category" && selectedFilter.key === "row_categories" && (
              <>
                {/* ROW Categories filter */}
                <div className="px-3">
                  {(filterData.row_categories || []).map((cat, idx) => (
                    <div key={cat.name} style={{ marginBottom: 12 }}>
                      <div className="font-semibold text-brand-700 mb-1">{cat.name}</div>
                      <ul className="list-none">
                        {cat.sub_categories.map((sub) => (
                          <li className="flex items-start" key={sub.id}>
                            <div className="pr-2">
                              <MuiCheckbox
                                onChange={() => handleCheckboxFilter(sub, "row_categories_id")}
                                checked={filters.row_categories_id?.includes(sub.id) || false}
                                sx={{
                                  paddingTop: 0,
                                  color: `var(--brand-200)`,
                                  "&.Mui-checked": { color: `#3A1C73` },
                                }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-800 pt-3">{sub.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Date Range filter */}
            {selectedFilter.type === "dateRange" && (
              <div className="px-6 py-4">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div className="grid gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-800 mb-2">
                        From
                      </p>
                      <MobileDatePicker
                        format="DD/MM/YYYY"
                        value={
                          dateRange[
                            selectedFilter.key as keyof typeof dateRange
                          ]?.start
                            ? dayjs(
                                dateRange[
                                  selectedFilter.key as keyof typeof dateRange
                                ].start,
                              )
                            : null
                        }
                        onChange={(newValue) =>
                          handleDateRangeChange(
                            "start",
                            newValue,
                            selectedFilter.key as keyof typeof DATE_RANGE_KEYS,
                          )
                        }
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            placeholder: "Start Creation Date",
                          },
                        }}
                      />
                    </div>

                    <div>
                      <p className="text-xs font-medium text-gray-800 mb-2">
                        To
                      </p>
                      <MobileDatePicker
                        format="DD/MM/YYYY"
                        value={
                          dateRange[
                            selectedFilter.key as keyof typeof dateRange
                          ]?.end
                            ? dayjs(
                                dateRange[
                                  selectedFilter.key as keyof typeof dateRange
                                ].end,
                              )
                            : null
                        }
                        onChange={(newValue) =>
                          handleDateRangeChange(
                            "end",
                            newValue,
                            selectedFilter.key as keyof typeof DATE_RANGE_KEYS,
                          )
                        }
                        minDate={
                          dateRange[
                            selectedFilter.key as keyof typeof dateRange
                          ]?.start
                            ? dayjs(
                                dateRange[
                                  selectedFilter.key as keyof typeof dateRange
                                ].start,
                              )
                            : undefined
                        }
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            placeholder: "End Creation Date",
                          },
                        }}
                      />
                    </div>
                  </div>
                </LocalizationProvider>
              </div>
            )}
          </div>
        </div>

        <div className="divider-solid"></div>
        <div className="flex justify-between mt-4 mx-4">
          <MuiButton
            type="button"
            name="Clear All"
            variant="outlined"
            onClick={handleClear}
            sx={{
              borderRadius: "6.25rem",
              fontWeight: `var(--font-medium)`,
              padding: "0.625rem 1rem",
              border: "1px solid #3A1C73",
              color: "#3A1C73",
            }}
          />
          <div>
            <MuiButton
              type="button"
              name="Cancel"
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderRadius: "6.25rem",
                padding: "0.625rem 1rem",
                mr: 1,
                border: "1px solid #3A1C73",
                color: "#3A1C73",
              }}
            />
            <MuiButton
              type="button"
              disabled={hasDateRangeError}
              name="Apply"
              variant="contained"
              onClick={handleSubmitLocal}
              sx={{
                background: `#3A1C73`,
                borderRadius: "6.25rem",
                fontWeight: `var(--font-medium)`,
                padding: "0.625rem 1rem",
              }}
            />
          </div>
        </div>
      </div>
    </ApplicationFilterModal>
  );
};

export default ApplicationFilter;
